import { useQueryClient } from '@tanstack/react-query';
import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import React, { forwardRef, useCallback, useEffect, useId, useImperativeHandle, useRef } from 'react';
import { toast } from 'sonner';
import { StickToBottomContext } from 'use-stick-to-bottom';
import { useErrorMessages, useUserSession } from '../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../models/Logging';
import { useAIAssistantCreateConversationMutation } from '../../../queries/AI/useAIAssistantCreateConversationMutation';
import {
	getConversationChatMessageQueryKey,
	useAIConversationChatMessageQuery,
} from '../../../queries/AIConversation/useAIConversationChatMessageQuery';
import {
	invalidateAllAIConversationFilterQueries,
	prependAIConversationToFilterCache,
	useAIConversationFilterQuery,
} from '../../../queries/AIConversation/useAIConversationFilterQuery';
import { AIConversationMessage } from '../../components-v2/ai/AIConversationMessage';
import {
	ChatConversation,
	ChatConversationContent,
	ChatConversationScrollButton,
} from '../../components-v2/ai/ChatConversation';
import {
	PromptInput,
	PromptInputBody,
	PromptInputFooter,
	PromptInputMessage,
	PromptInputSpeechButton,
	PromptInputSubmit,
	PromptInputTextarea,
	PromptInputTools,
} from '../../components-v2/ai/PromptInput';
import { AI_THINKING_MESSAGES, ThinkingIndicator } from '../../components-v2/ai/ThinkingIndicator';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import {
	AIChatAssistantMessage,
	removeErrorMessageFromConversationCache,
	UniversalAssistantLayout,
	useAIAssistantStreamingMutation,
} from '../templates/AssistantChat';
import { s as chatStyles } from '../templates/AssistantChat/styles';

const WEBSITE_EDITOR_AGENT_NAME = 'WebsiteEditorAgent';

// The agent uses the Default conversation type, which several agents share, so conversations are found by name.
const WEBSITE_EDITOR_CONVERSATION_FILTER: Api.AIConversationFilter = {
	criteria: [{ property: Api.AIConversationFilterProperty.AssistantName, value: WEBSITE_EDITOR_AGENT_NAME }],
};

export interface WebsiteAssistantChatHandle {
	/** False when the turn couldn't be sent, so the caller can keep the reader's selection and text. */
	submitElementEdit: (selection: { elementIndex: number; fieldPath?: string }, text: string) => boolean;
}

interface Props {
	/** The page currently shown in the preview; sent with every turn so "this page" needs no clarifying question. */
	currentPageId?: string;
	canEdit: boolean;
	onWebsiteChange: (change: Api.WebsiteChangeToolCall, userInitiated: boolean) => void;
}

/**
 * The chat beside the site preview. Same conversation plumbing as the other in-app assistants — find or create
 * the conversation, stream turns into the react-query cache — pointed at WebsiteEditorAgent, with the page in
 * view attached as a ResourceAIContext and change cards wired back to the preview.
 */
export const WebsiteAssistantChat = forwardRef<WebsiteAssistantChatHandle, Props>(function WebsiteAssistantChat(
	{ currentPageId, canEdit, onWebsiteChange }: Props,
	ref
) {
	const id = useId();
	const userSession = useUserSession();
	const queryClient = useQueryClient();
	const errorMessages = useErrorMessages();
	const logger = useEventLogging('WebsiteAssistantChat');
	const textareaRef = useRef<HTMLTextAreaElement>(null);
	const conversationRef = useRef<StickToBottomContext>(null);

	const conversationFilterQuery = useAIConversationFilterQuery({ filter: WEBSITE_EDITOR_CONVERSATION_FILTER });
	const conversationId = conversationFilterQuery.data?.[0]?.id;
	const chatMessageQuery = useAIConversationChatMessageQuery({
		enabled: Boolean(conversationId),
		id: conversationId,
		staleTime: Infinity,
	});

	const {
		mutate: createConversation,
		isPending: isCreating,
		isError: createFailed,
	} = useAIAssistantCreateConversationMutation({
		onSuccess: created => {
			prependAIConversationToFilterCache({ filter: WEBSITE_EDITOR_CONVERSATION_FILTER }, created);
			invalidateAllAIConversationFilterQueries();
		},
		onError: error => {
			errorMessages.pushApiError(error);
			logger.logApiError('Create-WebsiteEditor-Conversation-Error', error);
		},
	});

	// Without the failure latch a rejected create re-satisfies this the moment isCreating clears, looping POSTs.
	useEffect(() => {
		if (conversationFilterQuery.isSuccess && !conversationFilterQuery.data?.length && !isCreating && !createFailed) {
			createConversation({ assistantName: WEBSITE_EDITOR_AGENT_NAME });
		}
	}, [conversationFilterQuery.isSuccess, conversationFilterQuery.data, createConversation, isCreating, createFailed]);

	const streamingMutation = useAIAssistantStreamingMutation({ assistantName: WEBSITE_EDITOR_AGENT_NAME });
	const isBusy = streamingMutation.isLoading || streamingMutation.isStreaming;

	// The tool call only reaches us after the edit is committed and the draft preview cache invalidated, so the
	// announce-time reload is normally enough. That invalidation crosses API replicas over a backplane, though,
	// and a fetch landing before it arrives can still be served a cached render — this second pass covers that
	// window, and comes out once the propagation is made reliable at the source.
	const pendingChange = useRef<Api.WebsiteChangeToolCall | null>(null);
	const wasBusy = useRef(false);
	// Cards announce themselves on mount, so loading a conversation replays every past change. Only a change from
	// a turn taken here should move the preview — otherwise history overrides the page the reader deep-linked to.
	const hasStreamed = useRef(false);

	const handleWebsiteChange = useCallback(
		(change: Api.WebsiteChangeToolCall, userInitiated: boolean) => {
			if (!userInitiated) {
				if (!hasStreamed.current) {
					return;
				}
				pendingChange.current = change;
			}
			onWebsiteChange(change, userInitiated);
		},
		[onWebsiteChange]
	);

	useEffect(() => {
		if (isBusy) {
			hasStreamed.current = true;
		}
		if (wasBusy.current && !isBusy && pendingChange.current) {
			const change = pendingChange.current;
			pendingChange.current = null;
			onWebsiteChange(change, false);
		}
		wasBusy.current = isBusy;
	}, [isBusy, onWebsiteChange]);

	const contexts = (): Api.AIContext[] =>
		currentPageId
			? [{ _type: 'ResourceAIContext', resourceType: 'CmsPage', resourceId: currentPageId } as Api.ResourceAIContext]
			: [];

	const handleNewChatClick = () => createConversation({ assistantName: WEBSITE_EDITOR_AGENT_NAME });

	const handleTryAgain = (chatRequest: Api.AIChatRequest) => {
		if (!conversationId) {
			return;
		}
		removeErrorMessageFromConversationCache(conversationId);
		streamingMutation.mutate({ conversationId, chatRequest });
	};

	const sendTurn = (text: string, extraContexts: Api.AIContext[] = []) => {
		if (!conversationId || !text.trim()) {
			return false;
		}
		if (isBusy) {
			toast.error('Please wait for the current response to finish.');
			return false;
		}
		const contents: Api.AIChatContent[] = [{ _type: 'AITextContent', text } as Api.AITextContent];
		conversationRef.current?.scrollToBottom();
		removeErrorMessageFromConversationCache(conversationId);
		queryClient.setQueryData(
			getConversationChatMessageQueryKey({ id: conversationId }),
			(prev: Api.AIChatMessage[]) => [
				...(prev ?? []),
				{ role: Api.AIChatRole.User, contents, id: null } as Api.AIChatMessage,
			]
		);
		streamingMutation.mutate({
			conversationId,
			chatRequest: {
				chatMessages: [{ role: Api.AIChatRole.User, contents, id: null }],
				contexts: [...contexts(), ...extraContexts],
				includeProgress: true,
			} as Api.AIMessageRequest,
		});
		return true;
	};

	const handlePromptSubmit = ({ text }: PromptInputMessage) => {
		sendTurn(text);
	};

	// The reader's words go out verbatim. The element is named to the agent by the selection context, which the API
	// resolves from its own copy of the page — a label read out of the preview document would be untrusted text
	// reaching the model as the reader's own instruction.
	useImperativeHandle(ref, () => ({
		submitElementEdit: (selection, text) => {
			if (!currentPageId) {
				return false;
			}
			return sendTurn(text, [
				{
					_type: 'WebsiteSelectionAIContext',
					pageId: currentPageId,
					elementIndex: selection.elementIndex,
					fieldPath: selection.fieldPath,
				} as Api.WebsiteSelectionAIContext,
			]);
		},
	}));

	if (!conversationId) {
		return (
			<UniversalAssistantLayout title='Edit with Lev' onNewChat={handleNewChatClick}>
				<LoadingSpinner />
			</UniversalAssistantLayout>
		);
	}

	return (
		<UniversalAssistantLayout title='Edit with Lev' onNewChat={handleNewChatClick}>
			<ChatConversation contextRef={conversationRef}>
				<ChatConversationContent>
					<AIChatAssistantMessage>
						Hi {userSession.user.firstName},{' '}
						{canEdit
							? 'tell me what you’d like to change on your site and I’ll update it. You’ll see it on the right as soon as it’s done.'
							: 'ask me anything about what’s on your site. Editing isn’t turned on for your account yet — the Levitate team can enable it.'}
					</AIChatAssistantMessage>
					{chatMessageQuery.data?.map((message, i) => (
						<AIConversationMessage
							key={`${id}-message-${message.id ?? i}`}
							message={message}
							conversationId={conversationId}
							onErrorTryAgain={handleTryAgain}
							onWebsiteChange={handleWebsiteChange}
						/>
					))}
					{streamingMutation.isLoading ? (
						<AIChatAssistantMessage isThinking>
							<ThinkingIndicator messages={AI_THINKING_MESSAGES} />
						</AIChatAssistantMessage>
					) : null}
				</ChatConversationContent>
				<ChatConversationScrollButton />
			</ChatConversation>
			<div className={css(chatStyles.inputContainer)}>
				<PromptInput onSubmit={handlePromptSubmit} onError={err => toast.error(err.message)}>
					<PromptInputBody>
						<PromptInputTextarea
							ref={textareaRef}
							placeholder={canEdit ? 'e.g. Change the homepage heading to…' : 'Ask about your site…'}
						/>
					</PromptInputBody>
					<PromptInputFooter>
						<PromptInputTools>
							<PromptInputSpeechButton textareaRef={textareaRef} />
						</PromptInputTools>
						<PromptInputSubmit status={isBusy ? 'streaming' : 'idle'} disabled={isBusy} />
					</PromptInputFooter>
				</PromptInput>
			</div>
		</UniversalAssistantLayout>
	);
});
